import * as React from 'react'
import { castingCreate, castingPostSubmitRedirectUrl, castingUpdate } from '../../../services/models/castings.service'
import { castingStoreCanFocus, castingStoreGetProgressForStep, castingStoreGetStepTitle, castingStoreGetSummaryForStep, castingStoreHasError, castingStorePersistCustomerPlanExpirationAction, castingStoreToJson, castingStoreWillBroadcastIfCreatedByOperator, focusedInputs, StoreProps } from '../../../services/stores/casting.store'
import { I18nService } from '../../../services/utils/i18n.service'
import CastingCustomerComponent from '../../plugins/casting/finalize/casting-customer.component'
import CastingStepComponent from './casting-step.component'
import { useMountTooltipRef } from './casting-step-use-mount-tooltip-ref.hook'
import TooltipComponent from '../../shared/tooltip.component'
import FakeInputComponent from '../../shared/fake-input.component'
import { trackingOnCastingCreate } from '../../../services/utils/tracking.service'

type Props = StoreProps & { mobile?: boolean }

const i18nScoped = new I18nService(require('./casting-step-3.component.yml'))

export default function CastingStep3Component ({ data, store, mobile }: Props) {
  const { currentTooltip, mountTooltipRef } = useMountTooltipRef({ data, store, step: 3 })

  const submitForm = React.useCallback(async () => {
    // The submitForm function is called immediately after a mutation of the casting store, but the closure still references the old data (before the mutation).
    // As a consequence, data.customer may be outdated, so we fetch the latest version of the data from the mutated store.
    const { data, data: { offeredService, city, editedCastingId, castingType, market } } = store
    if (!city) throw { errors: { city: [i18nScoped.t('undefined_city')] }, full_messages: [i18nScoped.t('undefined_city')] } satisfies StoreProps['data']['errors']

    const lastGuardBeforeSubmit = focusedInputs
      .filter(progressKey => castingStoreCanFocus(data, progressKey) && castingStoreGetProgressForStep(data, store, progressKey) !== 100)
      .map(progressKey => castingStoreGetStepTitle(data, progressKey))

    if (lastGuardBeforeSubmit.length) {
      throw { errors: {}, full_messages: [i18nScoped.t('incomplete_step', { step_name: lastGuardBeforeSubmit[0] })] } satisfies StoreProps['data']['errors']
    }

    const body = castingStoreToJson(data)
    await castingStorePersistCustomerPlanExpirationAction(data)
    
    if (body.casting.id) {
      const json = await castingUpdate(body)
      window.location.href = castingPostSubmitRedirectUrl(offeredService.casting_mode, json.casting, editedCastingId)
    } else {
      const json = await castingCreate(market, body)
      trackingOnCastingCreate({
        casting: { slots: body.casting.slots, casting_type: castingType, quantity: body.casting.quantity },
        currency: market.currency,
        priceCents: json.casting.price_cents,
        offeredService: offeredService
      })

      window.location.href = castingPostSubmitRedirectUrl(offeredService.casting_mode, json.casting, editedCastingId, json.proposal_id)
    }
  }, [store]) // should only reference [store], so the `submitForm` function is immuable

  const { renderingMode, offeredService, editedCastingId, market } = data

  const finalButtonText = React.useMemo(() => {
    if (editedCastingId) {
      return i18nScoped.t('btn_text.modify')
    } else if (offeredService.casting_mode === 'direct') {
      return i18nScoped.t('btn_text.book_now')
    } else {
      return i18nScoped.t('btn_text.start')
    }
  }, [editedCastingId, offeredService.casting_mode])

  return <CastingStepComponent
    key="3"
    title={ i18nScoped.t(window.currentUser && window.currentUser.type !== 'Customer' ? 'title_not_customer' : 'title') }
    btnHidden
    className={ (renderingMode === 'mobile' ? 'mobile' : '') + ' js-casting-step-3' }
    tooltip={ mobile ? undefined : currentTooltip }>
    <CastingCustomerComponent store={ store } data={ data } submitButtonText={ finalButtonText } onIdentityResolved={ submitForm }>
      { castingStoreCanFocus(data, 'backoffice-service-delivery') && <div className="input--withIcon icon-shopping-cart u-mtm">
        <FakeInputComponent
          placeholder={ castingStoreGetStepTitle(data, 'backoffice-service-delivery') }
          onClick={ () => store.tryFocus('backoffice-service-delivery') }
          value={ castingStoreGetSummaryForStep(data, store, 'backoffice-service-delivery') }
          ref={ mountTooltipRef('backoffice-service-delivery') }
          className={ 'js-test-fake-service-delivery ' + (castingStoreHasError(data, 'backoffice-service-delivery') ? 'has-errors' : '') } />
      </div> }

      { castingStoreCanFocus(data, 'employee-quotation') && <div className={ `input--withIcon icon-${market.currency === 'EUR' ? 'euro' : 'dollar'} u-mtm` }>
        <FakeInputComponent
          placeholder={ castingStoreGetStepTitle(data, 'employee-quotation') }
          onClick={ () => store.tryFocus('employee-quotation') }
          value={ castingStoreGetSummaryForStep(data, store, 'employee-quotation') }
          ref={ mountTooltipRef('employee-quotation') }
          className={ 'js-test-fake-employee-quotation ' + (castingStoreHasError(data, 'employee-quotation') ? 'has-errors' : '') } />
      </div> }

      { castingStoreWillBroadcastIfCreatedByOperator(data) && <TooltipComponent className="inputCheckbox u-mtm" message={ i18nScoped.t('no_broadcast_tooltip') }>
        <input id="skip_broadcast_on_create" type="checkbox" checked={ data.skipBroadcastOnCreate } onChange={ event => store.set({ skipBroadcastOnCreate: event.target.checked }) } />
        <label htmlFor="skip_broadcast_on_create">{ i18nScoped.t('no_broadcast') }</label>
      </TooltipComponent> }
    </CastingCustomerComponent>
  </CastingStepComponent>
}
